/**
 * OrderService Bypass Modal
 *
 * Displays a warning modal when OrderService order is not found (412 error)
 * and allows the user to bypass the validation by providing a reason.
 */

import {
    Box,
    Button,
    FormField,
    Modal,
    SpaceBetween,
    Textarea,
} from '@amzn/awsui-components-react-v3';
import React, { useState } from 'react';
import { containsPII } from '../../../../common/utils/piiValidator';

export interface OrderServiceBypassModalProps {
    visible: boolean;
    onCancel: () => void;
    onBypass: (bypassReason: string) => void;
    isLoading?: boolean;
}

export const OrderServiceBypassModal: React.FC<
    OrderServiceBypassModalProps
> = ({ visible, onCancel, onBypass, isLoading = false }) => {
    const [bypassReason, setBypassReason] = useState('');
    const [error, setError] = useState('');
    const [piiError, setPiiError] = useState('');

    const handleBypass = () => {
        if (!bypassReason.trim()) {
            setError('Bypass reason is required');
            return;
        }
        if (piiError) {
            return;
        }
        onBypass(bypassReason.trim());
        // Clear state after successful bypass
        setBypassReason('');
        setError('');
        setPiiError('');
    };

    const handleTextareaChange = (value: string) => {
        setBypassReason(value);
        setError('');

        if (containsPII(value)) {
            setPiiError(
                'Please do not include personal information (email, phone, SSN, credit card)',
            );
        } else {
            setPiiError('');
        }
    };

    const handleCancel = () => {
        setBypassReason('');
        setError('');
        setPiiError('');
        onCancel();
    };

    return (
        <Modal
            visible={visible}
            onDismiss={handleCancel}
            header="Order Not Found"
            size="medium"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            variant="link"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleBypass}
                            loading={isLoading}
                            disabled={isLoading || !!piiError}
                        >
                            Bypass
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                <Box variant="p">
                    Order not found for Opportunity in OrderService. This may
                    indicate the order has not been ingested yet.
                </Box>
                <FormField
                    label="Bypass Reason"
                    description="Please provide a reason for bypassing the OrderService validation"
                    errorText={error || piiError}
                >
                    <Textarea
                        value={bypassReason}
                        onChange={({ detail }) =>
                            handleTextareaChange(detail.value)
                        }
                        placeholder="Enter reason for bypass..."
                        rows={4}
                        disabled={isLoading}
                        invalid={!!error || !!piiError}
                    />
                </FormField>
            </SpaceBetween>
        </Modal>
    );
};

export default OrderServiceBypassModal;
